import { z } from 'zod'
import { useMemo } from 'react'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { zSetting } from 'app/frontend/base/helpers/forms'

const formSchema = z.object({
  defaultTeamId: z.string().nullable(),
  timezone: z.string(),
  settings: z.object({
    guildNeighborhoodType: zSetting(z.enum(['guild', 'charter'])),
    displayApplyButtonInOverview: zSetting(z.boolean()),
    housingPageDisplay: zSetting(
      z.enum([
        'guild_and_team_pages',
        'guild_page_only',
        'team_pages_only',
        'nowhere',
      ]),
    ),
  }),
})

type GuildSettingsFormEntity = wowaudit.Guild & {
  defaultTeamId: string | null
}

const GuildSection = () => {
  const { guild } = useGuild()

  const timeZoneOptions = useMemo(() => {
    return Intl.supportedValuesOf('timeZone')
  }, [])

  if (!guild) return null

  return (
    <FormWrapper<GuildSettingsFormEntity>
      key={guild.id}
      formSchema={formSchema}
      entityType='guild'
      entity={{
        ...guild,
        defaultTeamId: guild.defaultTeam?.id.toString() ?? null,
      }}
      transformValues={(values) => ({
        ...values,
        defaultTeamId: values.defaultTeamId
          ? Number(values.defaultTeamId)
          : null,
      })}
      onSave={(form, data) => {
        form.reset({
          ...data,
          defaultTeamId: data.defaultTeam?.id?.toString() ?? null,
        })
      }}
    >
      <SelectField
        name='defaultTeamId'
        options={guild.teamReferences.map((team) => ({
          label: team.name,
          value: team.id.toString(),
        }))}
        customPlaceholder='No default team'
        transformOptions={false}
        isClearable={true}
      />

      <SelectField
        name='timezone'
        options={timeZoneOptions}
        isSearchable={true}
      />

      <SettingField name='guildNeighborhoodType' />
      <SettingField name='housingPageDisplay' />
      <SettingField name='displayApplyButtonInOverview' layout='box' />
    </FormWrapper>
  )
}

export default GuildSection
